import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { RouterLink, RouterLinkActive, RouterOutlet, Router } from '@angular/router';
import { ModalService } from '../../services/modal.service';
import { fromEvent, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { I18nTranslator } from '../../i18n/translate';
import { Farm } from '../../models/farm.model';
import { Page } from '../../models/page.model';
import { User } from '../../models/user.model';
import { FarmService } from '../../services/farm.service';
import { KeycloakService } from '../../services/keycloak.service';
import { UserService } from '../../services/user.service';
import { showErrorMessage, toNull } from '../../utils/utils';
import { FarmEditComponent } from './farm-edit.component';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PaginationModule } from 'ngx-bootstrap/pagination';

@Component({
  standalone: true,
  selector: 'farms-cmp',
  templateUrl: 'farms.component.html',
  imports: [RouterLink, RouterLinkActive, RouterOutlet, FarmEditComponent, TranslatePipe, CommonModule, FormsModule, PaginationModule],
  providers: [FarmService, UserService],
})
export class FarmComponent implements OnInit, AfterViewInit {
  farms: Farm[];

  error: any;

  @ViewChild('filter') filterElement: ElementRef;
  @ViewChild('filterOrga') filterOrgaElement: ElementRef;
  @ViewChild('termAdvisorFilter') termAdvisorFilter: ElementRef;

  public rows: Array<any> = [];

  public page: number = 1;
  public itemsPerPage: number = 10;
  public maxSize: number = 5;
  public numPages: number = 1;
  public length: number = 0;
  protected isAdmin = false;

  public filter: string;
  public filterOrga: string;
  public advisorFilter: string = null;

  public showRef: boolean;
  public isFarmer: boolean;
  public isNegoce: boolean;
  public isManager: boolean;

  static filterToApply: string;
  static filterOrgaToApply: string;
  static filterAdvisorToApply: string;
  public advisors: Array<User> = [];

  constructor(
    private farmService: FarmService,
    private i18nTranslator: I18nTranslator,
    private router: Router,
    private modal: ModalService,
    private userService: UserService,
  ) {
    this.isFarmer = KeycloakService.auth.authz.hasRealmRole('ROLE_FARMER');
    this.isManager = KeycloakService.auth.authz.hasRealmRole('ROLE_MANAGER');
    this.showRef =
      KeycloakService.auth.authz.hasRealmRole('ROLE_ADMIN') || KeycloakService.auth.authz.hasRealmRole('ROLE_SECTOR');
    this.isNegoce = KeycloakService.auth.authz.hasRealmRole('ROLE_NEGOCE');
    this.isAdmin = KeycloakService.auth.authz.hasRealmRole('ROLE_ADMIN');
  }

  getFarmsPaginated(
    pageIndex: number,
    sortColumnName: string,
    sortColumnOrder: string,
    filter: any,
    filterOrga: string,
  ) {
    this.farmService
      .getFarmsAdvanced(pageIndex, this.itemsPerPage, sortColumnName, sortColumnOrder, filter, filterOrga, null, null)
      .subscribe((page) => this.handlePage(page));
  }

  handlePage(page: Page<Farm>) {
    this.rows = page.content;
    this.numPages = page.totalPages;
    this.length = page.totalElements;
  }

  addFarm() {
    this.router.navigate(['/farms', 'new']);
  }

  deleteFarm(farm: Farm, event: any) {
    event.stopPropagation();
    this.farmService.delete(farm).subscribe(
      (res) => {
        this.onChangeTable();
      },
      (error) => this.handleDeleteError(error),
    );
  }

  editFarm(farm: Farm, event: any) {
    this.router.navigate(['/farms', 'edit', farm.id]);
  }

  ngOnInit() {}

  updateUIWithFilters() {
    if (FarmComponent.filterToApply) {
      this.filter = FarmComponent.filterToApply;
      this.filterElement.nativeElement.value = this.filter;
    }
    if (FarmComponent.filterOrgaToApply && this.filterOrgaElement) {
      this.filterOrga = FarmComponent.filterOrgaToApply;
      this.filterOrgaElement.nativeElement.value = this.filterOrga;
    }
    if (FarmComponent.filterAdvisorToApply && this.termAdvisorFilter) {
      this.advisorFilter = FarmComponent.filterAdvisorToApply;
      this.termAdvisorFilter.nativeElement.value = this.advisorFilter;
    }
  }

  ngAfterViewInit() {
    const observables = new Array<Observable<{}>>();
    fromEvent(this.filterElement.nativeElement, 'keyup')
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((term: any) => {
        this.filter = <string>term.target.value;
        this.updateFilters();
      });
    if (this.isAdmin || this.isNegoce) {
      fromEvent(this.filterOrgaElement.nativeElement, 'keyup')
        .pipe(debounceTime(500), distinctUntilChanged())
        .subscribe((term: any) => {
          this.filterOrga = <string>term.target.value;
          this.updateOrgaFilters();
        });
    }
    if (this.isAdmin || this.isNegoce || this.isManager) {
      fromEvent(this.termAdvisorFilter.nativeElement, 'keyup')
        .pipe(debounceTime(300), distinctUntilChanged())
        .subscribe((term: any) => {
          this.advisorFilter = <string>term.target.value;
          this.updateFilters();
        });
    }
    this.onChangeTable();
    this.updateUIWithFilters();
  }

  updateFilters() {
    FarmComponent.filterToApply = this.filter;
    if (this.isAdmin || this.isNegoce || this.isManager) {
      FarmComponent.filterAdvisorToApply = this.advisorFilter;
    }
    // reset la page à la première et update la table
    // FIXME faire mieux : suspendre le traitement des changements ?
    if (this.page === 1) {
      this.onChangeTable();
    } else {
      // le biding (pagechanged) du contrôle de pagination va appeler onChangeTable
      this.page = 1;
    }
  }

  updateOrgaFilters() {
    FarmComponent.filterOrgaToApply = this.filterOrga;
    // reset la page à la première et update la table
    // FIXME faire mieux : suspendre le traitement des changements ?
    if (this.page === 1) {
      this.onChangeTable();
    } else {
      // le biding (pagechanged) du contrôle de pagination va appeler onChangeTable
      this.page = 1;
    }
  }

  onChangeTable(page: any = { page: this.page, itemsPerPage: this.itemsPerPage }): any {
    const pageNumber = page.page;
    const filters = {
      filter: FarmComponent.filterToApply,
      filterAdvisor: FarmComponent.filterAdvisorToApply,
    };
    this.getFarmsPaginated(pageNumber, 'id', 'desc', filters, FarmComponent.filterOrgaToApply);
  }

  handleDeleteError(error: any) {
    const msg = JSON.parse(error._body);
    if (error.status === 400) {
      showErrorMessage(this.modal, msg.errorMessage);
    } else {
      showErrorMessage(this.modal, this.i18nTranslator.translate('farm.table.delete.error'));
    }
  }
}
